import { Point } from '../types';

interface Circle {
  center: Point;
  radius: number;
  confidence: number;
}

interface BoundingBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

/**
 * Calculate the bounding box of a set of points
 */
function getBoundingBox(points: Point[]): BoundingBox {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  points.forEach(point => {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  });

  const width = maxX - minX;
  const height = maxY - minY;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  return { minX, maxX, minY, maxY, width, height, centerX, centerY };
}

/**
 * Calculate the distance between two points
 */
function distance(p1: Point, p2: Point): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Detect if a stroke forms a circle-like shape (can be open)
 */
export function detectCircle(points: Point[]): Circle | null {
  // Need at least 20 points for a reasonable circle (very strict)
  if (points.length < 20) return null;

  // Get bounding box
  const bbox = getBoundingBox(points);
  
  // For very small strokes, skip detection (increased minimum size)
  if (bbox.width < 30 || bbox.height < 30) return null;
  
  // Check aspect ratio - circles should be roughly square (very strict)
  const aspectRatio = bbox.width / bbox.height;
  if (aspectRatio < 0.8 || aspectRatio > 1.2) return null;
  
  // Check if stroke is closed (start and end points should be close)
  const start = points[0];
  const end = points[points.length - 1];
  const gapDistance = distance(start, end);
  const avgDimension = (bbox.width + bbox.height) / 2;
  
  // If gap is too large relative to size, it's not a closed circle
  if (gapDistance > avgDimension * 0.3) return null;

  // Use bounding box center for better accuracy
  const center = { x: bbox.centerX, y: bbox.centerY, pressure: 0.5 };
  
  // Calculate average radius
  const distances = points.map(point => distance(point, center));
  const avgRadius = distances.reduce((sum, d) => sum + d, 0) / distances.length;
  
  // Filter out outliers (points too far from average)
  const filteredDistances = distances.filter(d => Math.abs(d - avgRadius) < avgRadius * 0.5);
  if (filteredDistances.length < points.length * 0.6) return null; // Too many outliers
  
  // Recalculate average with filtered points
  const filteredAvgRadius = filteredDistances.reduce((sum, d) => sum + d, 0) / filteredDistances.length;

  // Check if points form an arc around the center
  // Calculate the angular coverage
  const angles = points.map(point => 
    Math.atan2(point.y - center.y, point.x - center.x)
  );
  
  // Create angular histogram to check distribution
  const numBins = 8;
  const bins = new Array(numBins).fill(0);
  angles.forEach(angle => {
    const normalizedAngle = (angle + Math.PI) / (2 * Math.PI); // 0 to 1
    const binIndex = Math.floor(normalizedAngle * numBins) % numBins;
    bins[binIndex]++;
  });
  
  // Count how many bins have points
  const filledBins = bins.filter(count => count > 0).length;
  const angularCoverage = filledBins / numBins;
  
  // For circles, we need at least 5/8 bins filled (62.5% coverage) - very strict
  if (angularCoverage < 0.625) return null;

  // Calculate how consistent the radius is
  const radiusDeviations = filteredDistances.map(d => Math.abs(d - filteredAvgRadius));
  const avgRadiusDeviation = radiusDeviations.reduce((sum, d) => sum + d, 0) / radiusDeviations.length;
  const radiusConsistency = 1 - (avgRadiusDeviation / filteredAvgRadius);
  
  // Require much better radius consistency
  if (radiusConsistency < 0.7) return null;
  
  // Check that radius is reasonable compared to bounding box
  const expectedRadius = Math.min(bbox.width, bbox.height) / 2;
  const radiusRatio = filteredAvgRadius / expectedRadius;
  if (radiusRatio < 0.8 || radiusRatio > 1.2) return null;

  // Combined confidence based on angular coverage and radius consistency
  const confidence = (angularCoverage * 0.5 + radiusConsistency * 0.5);

  // Much higher threshold for very strict detection
  if (confidence > 0.65) {
    return {
      center,
      radius: filteredAvgRadius,
      confidence
    };
  }

  return null;
}

/**
 * Check if a point is inside a circle
 */
export function isPointInCircle(point: Point, circle: Circle): boolean {
  return distance(point, circle.center) <= circle.radius;
}

/**
 * Check if a rectangle overlaps with a circle
 */
export function doesRectOverlapCircle(
  rect: { x: number; y: number; width: number; height: number },
  circle: Circle
): boolean {
  // Find the closest point on the rectangle to the circle's center
  const closestX = Math.max(rect.x, Math.min(circle.center.x, rect.x + rect.width));
  const closestY = Math.max(rect.y, Math.min(circle.center.y, rect.y + rect.height));
  
  // Calculate the distance between the circle's center and this closest point
  const distanceX = circle.center.x - closestX;
  const distanceY = circle.center.y - closestY;
  
  // If the distance is less than the circle's radius, an intersection occurs
  const distanceSquared = (distanceX * distanceX) + (distanceY * distanceY);
  return distanceSquared < (circle.radius * circle.radius);
}

/**
 * Check if points surround a specific area
 */
function doPointsSurroundArea(
  points: Point[],
  targetX: number,
  targetY: number,
  targetWidth: number,
  targetHeight: number
): boolean {
  // First check if this is likely a straight line (elimination gesture)
  if (points.length >= 2) {
    const start = points[0];
    const end = points[points.length - 1];
    const pathLength = calculatePathLength(points);
    const directDistance = distance(start, end);
    
    // If path is straight at all (>50% efficiency), it's likely a line, not a surrounding gesture
    // For selection, we expect curved paths
    const pathEfficiency = directDistance / pathLength;
    if (pathEfficiency > 0.5) {
      
      return false;
    }
  }
  
  // Check if we have points on multiple sides of the target
  let hasPointsLeft = false;
  let hasPointsRight = false;
  let hasPointsTop = false;
  let hasPointsBottom = false;
  
  // Also count how many points are on each side
  let pointsLeft = 0;
  let pointsRight = 0;
  let pointsTop = 0;
  let pointsBottom = 0;
  
  points.forEach(point => {
    if (point.x < targetX) {
      hasPointsLeft = true;
      pointsLeft++;
    }
    if (point.x > targetX + targetWidth) {
      hasPointsRight = true;
      pointsRight++;
    }
    if (point.y < targetY) {
      hasPointsTop = true;
      pointsTop++;
    }
    if (point.y > targetY + targetHeight) {
      hasPointsBottom = true;
      pointsBottom++;
    }
  });
  
  // Need points on at least 3 sides to consider it surrounded
  const sidesCount = [hasPointsLeft, hasPointsRight, hasPointsTop, hasPointsBottom].filter(Boolean).length;
  
  // Additionally, for a proper surrounding gesture, we need a good distribution of points
  // A diagonal line might have points on opposite sides but won't have a good distribution
  const minPointsPerSide = Math.max(2, points.length * 0.1); // At least 10% of points per side
  
  // Check if we have enough points on multiple sides
  const sidesWithEnoughPoints = [
    pointsLeft >= minPointsPerSide,
    pointsRight >= minPointsPerSide,
    pointsTop >= minPointsPerSide,
    pointsBottom >= minPointsPerSide
  ].filter(Boolean).length;
  
 
  
  // Require at least 3 sides with points AND at least 2 sides with enough points
  return sidesCount >= 3 && sidesWithEnoughPoints >= 2;
}

/**
 * Find which optical choice (if any) is circled by the given stroke
 */
export function findCircledOpticalChoice(
  points: Point[],
  opticalChoices: Array<{ id: string; x: number; y: number; width: number; height: number }>
): string | null {
  
  // First check if this is a straight line - if so, it's NOT a selection
  const lineDetection = detectDiagonalLine(points);
  if (lineDetection && lineDetection.confidence > 0.15) { // Very low threshold - any line-like stroke is rejected
   
    return null;
  }
  
  // First try circle detection
  const circle = detectCircle(points);
  
  if (circle) {
    // Check each optical choice
    for (const choice of opticalChoices) {
      // Check if the optical choice rectangle overlaps with the circle
      if (doesRectOverlapCircle(choice, circle)) {
        // Additional check: ensure the circle mostly contains the choice
        const choiceCenter = {
          x: choice.x + choice.width / 2,
          y: choice.y + choice.height / 2,
          pressure: 0.5
        };
        
        if (isPointInCircle(choiceCenter, circle)) {
          return choice.id;
        }
      }
    }
  }
  
  // If circle detection fails, try a simpler surrounding check
  // This handles cases where the user draws a rough shape around the choice
  for (const choice of opticalChoices) {
    const surrounded = doPointsSurroundArea(points, choice.x, choice.y, choice.width, choice.height);
    
    if (surrounded) {
      // Additional validation: check if most points are reasonably close to the choice
      const expandedBounds = {
        x: choice.x - 30,
        y: choice.y - 30,
        width: choice.width + 60,
        height: choice.height + 60
      };
      
      const pointsNearChoice = points.filter(point => 
        point.x >= expandedBounds.x && 
        point.x <= expandedBounds.x + expandedBounds.width &&
        point.y >= expandedBounds.y && 
        point.y <= expandedBounds.y + expandedBounds.height
      );
      
      // If at least 80% of points are near the choice, consider it selected
      // For selection, we expect careful drawing around the choice
      if (pointsNearChoice.length / points.length > 0.8) {
        return choice.id;
      }
    }
  }

  return null;
}

/**
 * Calculate the total path length of a stroke
 */
function calculatePathLength(points: Point[]): number {
  let totalLength = 0;
  for (let i = 1; i < points.length; i++) {
    totalLength += distance(points[i - 1], points[i]);
  }
  return totalLength;
}

/**
 * Calculate how much a path deviates from a straight line
 */
function calculatePathDeviation(points: Point[]): number {
  if (points.length < 3) return 0;
  
  const start = points[0];
  const end = points[points.length - 1];
  
  // Calculate perpendicular distance of each point from the line
  let maxDeviation = 0;
  
  for (let i = 1; i < points.length - 1; i++) {
    const point = points[i];
    
    // Calculate perpendicular distance from point to line
    const lineLength = distance(start, end);
    if (lineLength === 0) continue;
    
    const t = ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) / (lineLength * lineLength);
    const projX = start.x + t * (end.x - start.x);
    const projY = start.y + t * (end.y - start.y);
    
    const deviation = distance(point, { x: projX, y: projY, pressure: 0.5 });
    maxDeviation = Math.max(maxDeviation, deviation);
  }
  
  return maxDeviation;
}

/**
 * Detect if a stroke is a straight line (diagonal or horizontal) for elimination
 */
export function detectDiagonalLine(points: Point[]): { angle: number; length: number; confidence: number } | null {
  if (points.length < 2) {
    return null;
  }
  
  // Get start and end points
  const start = points[0];
  const end = points[points.length - 1];
  
  // Calculate direct distance and path length
  const directDistance = distance(start, end);
  const pathLength = calculatePathLength(points);
  
  // Minimum distance to detect a meaningful line (increased from 2)
  if (directDistance < 5) {
    return null;
  }
  
  // Calculate angle in degrees
  const angleRadians = Math.atan2(end.y - start.y, end.x - start.x);
  const angle = angleRadians * 180 / Math.PI;
  
  // Calculate straightness metrics first - this is the most important
  const pathEfficiency = directDistance / pathLength; // How direct the path is
  const maxDeviation = calculatePathDeviation(points);
  const deviationRatio = maxDeviation / directDistance;
  
  // Check if it's a straight line based on path efficiency and deviation
  // These are the key metrics for detecting ANY straight line
  // For elimination, we're VERY lenient - students draw messy lines
  if (pathEfficiency < 0.3) { // Only 30% efficiency required - very messy lines accepted
    return null;
  }
  
  if (deviationRatio > 0.6) { // Allow up to 60% deviation - very curved lines accepted
    return null;
  }
  
  // If we get here, it's a straight line! Now calculate confidence
  // Confidence is based purely on how straight the line is
  const straightnessConfidence = pathEfficiency;
  const deviationConfidence = 1 - deviationRatio;
  const confidence = (straightnessConfidence * 0.6 + deviationConfidence * 0.4);
  
  // Return the detected line with its actual angle
  return { 
    angle: angle, // Return the actual angle, not normalized
    length: directDistance,
    confidence 
  };
}

/**
 * Check if a line segment intersects with a rectangle
 */
function lineIntersectsRect(
  lineStart: Point,
  lineEnd: Point,
  rect: { x: number; y: number; width: number; height: number }
): boolean {
  // Check if either endpoint is inside the rectangle
  if ((lineStart.x >= rect.x && lineStart.x <= rect.x + rect.width &&
       lineStart.y >= rect.y && lineStart.y <= rect.y + rect.height) ||
      (lineEnd.x >= rect.x && lineEnd.x <= rect.x + rect.width &&
       lineEnd.y >= rect.y && lineEnd.y <= rect.y + rect.height)) {
    return true;
  }
  
  // Check line intersection with each edge of the rectangle
  const edges = [
    { start: { x: rect.x, y: rect.y }, end: { x: rect.x + rect.width, y: rect.y } },
    { start: { x: rect.x + rect.width, y: rect.y }, end: { x: rect.x + rect.width, y: rect.y + rect.height } },
    { start: { x: rect.x + rect.width, y: rect.y + rect.height }, end: { x: rect.x, y: rect.y + rect.height } },
    { start: { x: rect.x, y: rect.y + rect.height }, end: { x: rect.x, y: rect.y } }
  ];
  
  for (const edge of edges) {
    if (linesIntersect(lineStart, lineEnd, edge.start, edge.end)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if two line segments intersect
 */
function linesIntersect(p1: Point, p2: Point, p3: { x: number; y: number }, p4: { x: number; y: number }): boolean {
  const x1 = p1.x, y1 = p1.y;
  const x2 = p2.x, y2 = p2.y;
  const x3 = p3.x, y3 = p3.y;
  const x4 = p4.x, y4 = p4.y;
  
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 0.000001) return false; // Lines are parallel
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

/**
 * Calculate how much of a line passes through a rectangle
 */
function calculateLineCoverage(
  points: Point[],
  rect: { x: number; y: number; width: number; height: number }
): number {
  let pointsInside = 0;
  
  for (const point of points) {
    if (point.x >= rect.x && point.x <= rect.x + rect.width &&
        point.y >= rect.y && point.y <= rect.y + rect.height) {
      pointsInside++;
    }
  }
  
  return pointsInside / points.length;
}

/**
 * Find which optical choice (if any) is crossed out by a diagonal line
 */
export function findEliminatedOpticalChoice(
  points: Point[],
  opticalChoices: Array<{ id: string; x: number; y: number; width: number; height: number }>
): string | null {


  
  const diagonalLine = detectDiagonalLine(points);
  if (!diagonalLine) {
   
    return null;
  }
  
  // Lower confidence requirement for easier elimination
  if (diagonalLine.confidence < 0.2) { // Very low threshold - accept messy elimination lines
   
    return null;
  }
  
  // Also check if line is long enough to be an elimination gesture
  const lineLength = Math.sqrt(
    Math.pow(points[points.length - 1].x - points[0].x, 2) + 
    Math.pow(points[points.length - 1].y - points[0].y, 2)
  );
  
  if (lineLength < 10) { // Reduced to 10 for easier elimination
    return null;
  }
  
  // Check stroke velocity to ensure it's a deliberate line gesture
  const startTime = points[0]?.timestamp || 0;
  const endTime = points[points.length - 1]?.timestamp || 0;
  const duration = endTime - startTime;
  const velocity = duration > 0 ? lineLength / duration : 0;
  
  // Removed velocity check - it was too restrictive
  
 
  
  // Get line endpoints
  const start = points[0];
  const end = points[points.length - 1];
  
  // Find best matching choice
  let bestChoice: string | null = null;
  let bestScore = 0;
  
  for (const choice of opticalChoices) {
       
    // Scale tolerance based on choice size - increased for easier elimination
    const tolerance = Math.min(choice.width, choice.height) * 0.4;
    const expandedChoice = {
      x: choice.x - tolerance,
      y: choice.y - tolerance,
      width: choice.width + tolerance * 2,
      height: choice.height + tolerance * 2
    };
 
    
    // Check if line intersects the choice
    const intersects = lineIntersectsRect(start, end, expandedChoice);
   
    
    if (!intersects) {
   
      continue;
    }
    
    // Calculate coverage
    const coverage = calculateLineCoverage(points, expandedChoice);
    
    
    // No minimum coverage requirement for elimination
    // Even a single point touching the choice can eliminate it
    if (coverage <= 0) {
      continue;
    }
    
    // Check diagonal crossing pattern
    // Find which quadrants the line enters and exits
    const centerX = choice.x + choice.width / 2;
    const centerY = choice.y + choice.height / 2;

    
    const startQuadrant = {
      left: start.x < centerX,
      top: start.y < centerY
    };
    
    const endQuadrant = {
      left: end.x < centerX,
      top: end.y < centerY
    };
    
    
    
    // Diagonal lines should cross opposite quadrants
    const crossesDiagonally = (startQuadrant.left !== endQuadrant.left) && 
                             (startQuadrant.top !== endQuadrant.top);
   
    
    // Calculate score based on coverage and diagonal crossing
    // Boost score for short strokes that clearly cross the choice
    const coverageBoost = points.length <= 15 ? 2.5 : 1.5;
    
    // Give high weight to diagonal crossing, but don't penalize non-diagonal lines too much
    const crossingBoost = crossesDiagonally ? 1.5 : 1.0;
    
    // For lines with good coverage (>40%), give extra boost
    const coverageQualityBoost = coverage >= 0.4 ? 1.2 : 1.0;
    
    const score = coverage * coverageBoost * crossingBoost * coverageQualityBoost * diagonalLine.confidence;
 
    
    if (score > bestScore) {
      bestScore = score;
      bestChoice = choice.id;

    }
  }
  
  // Very low score threshold for easy elimination
  const minScoreThreshold = 0.05; // Extremely low threshold
  
  const result = bestScore > minScoreThreshold ? bestChoice : null;
  
   
  return result;
}

/**
 * Detect if multiple strokes form an X pattern for elimination
 */
export function detectXPattern(
  strokes: Array<{ points: Point[]; timestamp: number }>,
  timeWindowMs: number = 2000
): boolean {
  if (strokes.length < 2) return false;
  
  // Get the most recent strokes within time window
  const now = Date.now();
  const recentStrokes = strokes.filter(s => now - s.timestamp <= timeWindowMs);
  
  if (recentStrokes.length < 2) return false;
  
  // Check the last two strokes
  const stroke1 = recentStrokes[recentStrokes.length - 2];
  const stroke2 = recentStrokes[recentStrokes.length - 1];
  
  const diagonal1 = detectDiagonalLine(stroke1.points);
  const diagonal2 = detectDiagonalLine(stroke2.points);
  
  if (!diagonal1 || !diagonal2) return false;
  
  // Check if the angles form an X (roughly perpendicular)
  const angleDiff = Math.abs(diagonal1.angle - diagonal2.angle);
  const isPerp = (angleDiff >= 70 && angleDiff <= 110);
  
  if (!isPerp) return false;
  
  // Check if the strokes overlap in space
  const bbox1 = getBoundingBox(stroke1.points);
  const bbox2 = getBoundingBox(stroke2.points);
  
  const overlapX = Math.max(0, Math.min(bbox1.maxX, bbox2.maxX) - Math.max(bbox1.minX, bbox2.minX));
  const overlapY = Math.max(0, Math.min(bbox1.maxY, bbox2.maxY) - Math.max(bbox1.minY, bbox2.minY));
  
  const overlap1 = (overlapX * overlapY) / (bbox1.width * bbox1.height);
  const overlap2 = (overlapX * overlapY) / (bbox2.width * bbox2.height);
  
  // Require significant overlap
  return Math.max(overlap1, overlap2) > 0.3;
}